/**
 * Data table for reports.
 */
import * as React from 'react';
import { FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CollapseReplaceHiddenContext } from '@/components/common/CollapseReplaceWrapper';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { DataTableFilters } from '@/components/common/data-table/DataTableFilters';
import { DataTable } from '@/components/common/data-table';
import { Button } from '@/components/forms/Button';
import { ReportRow } from './ReportRow';
import { ReportCard } from './ReportCard';
import { ReportFiltersContent } from './ReportFilters';
import { DeleteModal } from '@/components/common/modals/DeleteModal';
import { ValidateModal } from '@/components/common/modals/ValidateModal';
import { ReportValidationModal } from './ReportValidationModal';
import { useDataTable } from '@/hooks/useDataTable';
import { shouldRetryUnitGet, useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import {
  getAllReports,
  deleteReport,
  validateReport,
  exportReportsExcel,
} from '@/services/inperson/reports.service';
import { usePageActions } from '@/hooks/usePageActions';
import { cn } from '@/lib/utils';
import {
  DEFAULT_REPORT_FILTERS,
  ALL_FILTER_GROUP_IDS,
  FILTER_GROUP_LABELS,
} from '@/components/reports/report-configs';
import {
  findReportById,
  formatReportCountLabel,
  formatReportSessionModalItemName,
  getReportBulkDeleteDescription,
  getReportBulkDeleteTooltip,
  getReportBulkValidateDescription,
  getReportBulkValidateTooltip,
  getReportExportTooltip,
  getReportExpandedColSpan,
  getReportSelectionFlags,
  hasReportActiveFilters,
  isReportListEmptyForExport,
  mergeReportPageActions,
  resolveReportEffectiveSessionTypes,
  resolveReportTableRows,
  toReportApiFilterParams,
  getReportBulkDeleteSuccessMessage,
  getReportBulkValidateSuccessMessage,
  toggleListItem,
  canDeleteSessionReport,
  canValidateSessionReport,
  isSessionReportSelectable,
  resolveBulkValidateTarget,
  resolveSingleValidateTarget,
  type ReportPageActions,
} from '@/components/reports/inperson/report-table-utils';
import type { ViewMode } from '@/types/data-table.types';
import type { ElementWidth } from '@/types/interface-elements.types';
import type { ReportFilters, SessionReport } from '@/types/inperson/report.types';
import { ReportDetails } from '@/components/reports/inperson/renderReportDetails';
import { type ReportValidationOptions } from '@/utils/report-author-feedback-utils';
import { useConnectedUser } from '@/hooks/useConnectedUser';
import { useTableSelection } from '@/hooks/useTableSelection';
import { DataTableHeadCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';

/** Props for {@link ReportsDataTable}. */
interface ReportsDataTableProps {
  reports?: SessionReport[];
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  expandedRowId?: string | null;
  onToggleRow?: (id: string) => void;
  onCloseDetails?: () => void;
  onEditReport?: (sessionId: string, reportId: string, sessionType?: string) => void;
  enableFilters?: boolean;
  defaultFilters?: ReportFilters;
  layoutWidth?: ElementWidth;
  isSection?: boolean;
  pageActions?: { canDeleteRow?: boolean };
  /** Deep-link from URL (e.g. notification): API filter + fetch detail if the row is not on the current page. */
  deepLinkSessionId?: string | null;
  /** Restrict the list to reports of a parent session group (collective group report button). */
  deepLinkSessionGroupId?: string | null;
  activeTab?: string;
  /** RBAC: tabs the current user is allowed to see (restricts the all-tab query). */
  allowedTabIds?: string[];
}

/** Filter setters that always reset pagination to page 1. */
function useReportFilterHandlers(dt: ReturnType<typeof useDataTable<ReportFilters, string>>) {
  const resetPage = React.useCallback(() => dt.setCurrentPage(1), [dt]);

  const toggleStatusFilter = React.useCallback(
    (status: string) => {
      dt.setFilters((prev) => ({ ...prev, statuses: toggleListItem(prev.statuses, status) }));
      resetPage();
    },
    [dt, resetPage],
  );

  const toggleRatingFilter = React.useCallback(
    (rating: number) => {
      dt.setFilters((prev) => ({ ...prev, ratings: toggleListItem(prev.ratings, rating) }));
      resetPage();
    },
    [dt, resetPage],
  );

  const setDateRange = React.useCallback(
    (range: ReportFilters['dateRange']) => {
      dt.setFilters((prev) => ({ ...prev, dateRange: range }));
      resetPage();
    },
    [dt, resetPage],
  );

  const setCustomDateRange = React.useCallback(
    (from: Date | undefined, to: Date | undefined) => {
      dt.setFilters((prev) => ({ ...prev, customDateFrom: from, customDateTo: to }));
      resetPage();
    },
    [dt, resetPage],
  );

  const toggleSessionTypeFilter = React.useCallback(
    (type: string) => {
      dt.setFilters((prev) => ({
        ...prev,
        sessionTypes: toggleListItem(prev.sessionTypes, type),
      }));
      resetPage();
    },
    [dt, resetPage],
  );

  const setEstablishmentFilter = React.useCallback(
    (id: string | undefined) => {
      dt.setFilters((prev) => ({
        ...prev,
        establishmentIds: id ? [id] : [],
      }));
      resetPage();
    },
    [dt, resetPage],
  );

  const setSearch = React.useCallback(
    (value: string | undefined) => {
      dt.setFilters((prev) => ({ ...prev, search: value }));
      resetPage();
    },
    [dt, resetPage],
  );

  return React.useMemo(
    () => ({
      toggleStatusFilter,
      toggleRatingFilter,
      setDateRange,
      setCustomDateRange,
      toggleSessionTypeFilter,
      setEstablishmentFilter,
      setSearch,
    }),
    [
      toggleStatusFilter,
      toggleRatingFilter,
      setDateRange,
      setCustomDateRange,
      toggleSessionTypeFilter,
      setEstablishmentFilter,
      setSearch,
    ],
  );
}

/** Row/bulk delete and validate actions plus the modal state they drive. */
function useReportActions(args: {
  data: SessionReport[];
  userId: string | undefined;
  pageActionsForRows: ReportPageActions;
  canValidateAny: boolean;
}) {
  const queryClient = useQueryClient();
  const { data, userId, pageActionsForRows, canValidateAny } = args;
  const selection = useTableSelection();

  const [deleteId, setDeleteId] = React.useState<{
    sessionId: string;
    reportId: string;
  } | null>(null);
  const [validateId, setValidateId] = React.useState<{
    sessionId: string;
    reportId: string;
  } | null>(null);
  const [validationTarget, setValidationTarget] = React.useState<{
    sessionId: string;
    reportId: string;
    validateOptions: ReportValidationOptions;
  } | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkValidateOpen, setBulkValidateOpen] = React.useState(false);
  const [bulkValidationOpen, setBulkValidationOpen] = React.useState(false);
  const [bulkReportValidationOptions, setBulkReportValidationOptions] = React.useState<
    Record<string, ReportValidationOptions>
  >({});
  const [isPreparingBulkValidate, setIsPreparingBulkValidate] = React.useState(false);

  const selectableReports = React.useMemo(
    () =>
      data.filter((report) =>
        isSessionReportSelectable(report, canValidateAny, pageActionsForRows),
      ),
    [data, canValidateAny, pageActionsForRows],
  );
  const selectableIds = React.useMemo(
    () => selectableReports.map((report) => report.id),
    [selectableReports],
  );

  const selectableIdsKey = selectableIds.join('\0');
  React.useEffect(() => {
    selection.pruneTo(selectableIds);
  }, [selectableIdsKey, selectableIds, selection.pruneTo]);

  const selectedReports = React.useMemo(
    () => data.filter((report) => selection.selectedIds.includes(report.id)),
    [data, selection.selectedIds],
  );
  const selectedDeletableReports = React.useMemo(
    () => selectedReports.filter((report) => canDeleteSessionReport(report, pageActionsForRows)),
    [selectedReports, pageActionsForRows],
  );
  const selectedValidatableReports = React.useMemo(
    () => selectedReports.filter((report) => canValidateSessionReport(report, canValidateAny)),
    [selectedReports, canValidateAny],
  );

  const { allSelectableSelected, someSelectableSelected } = getReportSelectionFlags(
    selectableIds,
    selection.selectedIds,
  );

  const deleteMutation = useMutation({
    mutationFn: ({ sessionId, reportId }: { sessionId: string; reportId: string }) =>
      deleteReport(sessionId, reportId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Rapport supprimé avec succès !');
      setDeleteId(null);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const validateMutation = useMutation({
    mutationFn: ({ sessionId, reportId }: { sessionId: string; reportId: string }) =>
      validateReport(sessionId, reportId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Compte rendu validé avec succès !');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (reportsToDelete: SessionReport[]) => {
      await Promise.all(reportsToDelete.map((report) => deleteReport(report.sessionId, report.id)));
    },
    onSuccess: async (_result, reportsToDelete) => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(getReportBulkDeleteSuccessMessage(reportsToDelete.length));
      selection.clear();
      setBulkDeleteOpen(false);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const bulkValidateMutation = useMutation({
    mutationFn: async (reportsToValidate: SessionReport[]) => {
      await Promise.all(
        reportsToValidate.map((report) => validateReport(report.sessionId, report.id)),
      );
    },
    onSuccess: async (_result, reportsToValidate) => {
      await queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success(getReportBulkValidateSuccessMessage(reportsToValidate.length));
      selection.clear();
      setBulkValidateOpen(false);
      setBulkValidationOpen(false);
      setBulkReportValidationOptions({});
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleDeleteClick = React.useCallback((sessionId: string, reportId: string) => {
    setDeleteId({ sessionId, reportId });
  }, []);

  const handleDeleteConfirm = React.useCallback(() => {
    if (!deleteId) return;
    deleteMutation.mutate(deleteId);
  }, [deleteId, deleteMutation]);

  /** Open the validation modal for one report (or skip it when the modal is unnecessary). */
  const handleValidateClick = React.useCallback(
    async (sessionId: string, reportId: string) => {
      const report = findReportById(data, reportId);
      if (!report) return;
      const target = await resolveSingleValidateTarget(report, sessionId, userId);
      if (target.kind === 'incomplete') {
        setValidationTarget(target);
        return;
      }
      setValidateId(target);
    },
    [data, userId],
  );

  const handleValidationConfirm = React.useCallback(() => {
    if (!validationTarget) return;
    validateMutation.mutate({
      sessionId: validationTarget.sessionId,
      reportId: validationTarget.reportId,
    });
    setValidationTarget(null);
  }, [validationTarget, validateMutation]);

  /** Confirm validation after the author-feedback modal. */
  const handleValidateConfirm = React.useCallback(() => {
    if (!validateId) return;
    validateMutation.mutate(validateId);
    setValidateId(null);
  }, [validateId, validateMutation]);

  const handleToggleSelectAll = React.useCallback(() => {
    if (allSelectableSelected) {
      selection.clear();
      return;
    }
    selection.selectAll(selectableIds);
  }, [allSelectableSelected, selectableIds, selection]);

  const handleBulkDeleteConfirm = React.useCallback(() => {
    if (selectedDeletableReports.length === 0) return;
    bulkDeleteMutation.mutate(selectedDeletableReports);
  }, [selectedDeletableReports, bulkDeleteMutation]);

  const handleBulkValidateConfirm = React.useCallback(() => {
    if (selectedValidatableReports.length === 0) return;
    bulkValidateMutation.mutate(selectedValidatableReports);
  }, [selectedValidatableReports, bulkValidateMutation]);

  const handleBulkValidationConfirm = React.useCallback(() => {
    setBulkValidateOpen(true);
  }, []);

  const handleToolbarBulkValidateClick = React.useCallback(async () => {
    if (selectedValidatableReports.length === 0 || isPreparingBulkValidate) return;

    setIsPreparingBulkValidate(true);
    try {
      const target = await resolveBulkValidateTarget(selectedValidatableReports, userId);
      if (target.kind === 'incomplete') {
        setBulkReportValidationOptions(target.optionsByReportId);
        setBulkValidationOpen(true);
        return;
      }
      setBulkValidateOpen(true);
    } finally {
      setIsPreparingBulkValidate(false);
    }
  }, [selectedValidatableReports, isPreparingBulkValidate, userId]);

  return {
    selection,
    deleteId,
    setDeleteId,
    validateId,
    setValidateId,
    validationTarget,
    setValidationTarget,
    bulkDeleteOpen,
    setBulkDeleteOpen,
    bulkValidateOpen,
    setBulkValidateOpen,
    bulkValidationOpen,
    setBulkValidationOpen,
    bulkReportValidationOptions,
    setBulkReportValidationOptions,
    isPreparingBulkValidate,
    selectableIds,
    selectedDeletableReports,
    selectedValidatableReports,
    allSelectableSelected,
    someSelectableSelected,
    deleteMutation,
    validateMutation,
    bulkDeleteMutation,
    bulkValidateMutation,
    handleDeleteClick,
    handleDeleteConfirm,
    handleValidateClick,
    handleValidationConfirm,
    handleValidateConfirm,
    handleToggleSelectAll,
    handleBulkDeleteConfirm,
    handleBulkValidateConfirm,
    handleBulkValidationConfirm,
    handleToolbarBulkValidateClick,
  };
}

/** Column headers including the optional bulk-select checkbox. */
function ReportsTableHead({
  showBulkSelection,
  allSelectableSelected,
  someSelectableSelected,
  selectableDisabled,
  onToggleSelectAll,
}: Readonly<{
  showBulkSelection: boolean;
  allSelectableSelected: boolean;
  someSelectableSelected: boolean;
  selectableDisabled: boolean;
  onToggleSelectAll: () => void;
}>) {
  return (
    <tr>
      {showBulkSelection && (
        <th className="w-10 text-center">
          <div className="flex items-center justify-center">
            <DataTableHeadCheckbox
              checked={allSelectableSelected}
              indeterminate={someSelectableSelected}
              disabled={selectableDisabled}
              onCheckedChange={onToggleSelectAll}
            />
          </div>
        </th>
      )}
      <th>Séance</th>
      <th>Auteur</th>
      <th>Note</th>
      <th>Statut</th>
      <th>Date saisie</th>
      <th className="w-20 text-center">Actions</th>
    </tr>
  );
}

/** Bulk validate/delete, filters toggle, and Excel export. */
function ReportsToolbar({
  showBulkSelection,
  canValidateAny,
  canDeleteRow,
  canExport,
  enableFilters,
  selectedValidatableCount,
  selectedDeletableCount,
  bulkValidatePending,
  bulkDeletePending,
  isPreparingBulkValidate,
  showFilters,
  hasActiveFilters,
  isExporting,
  isEmptyForExport,
  onBulkValidate,
  onBulkDelete,
  onToggleFilters,
  onExportExcel,
}: Readonly<{
  showBulkSelection: boolean;
  canValidateAny: boolean;
  canDeleteRow: boolean;
  canExport: boolean;
  enableFilters: boolean;
  selectedValidatableCount: number;
  selectedDeletableCount: number;
  bulkValidatePending: boolean;
  bulkDeletePending: boolean;
  isPreparingBulkValidate: boolean;
  showFilters: boolean;
  hasActiveFilters: boolean;
  isExporting: boolean;
  isEmptyForExport: boolean;
  onBulkValidate: () => void;
  onBulkDelete: () => void;
  onToggleFilters: () => void;
  onExportExcel: () => void;
}>) {
  return (
    <>
      {showBulkSelection && canValidateAny && (
        <Button
          preset="toolbar-validate"
          disabled={
            selectedValidatableCount === 0 ||
            bulkValidatePending ||
            bulkDeletePending ||
            isPreparingBulkValidate
          }
          onClick={onBulkValidate}
          loading={isPreparingBulkValidate}
          tooltipText={getReportBulkValidateTooltip(selectedValidatableCount)}
        />
      )}
      {showBulkSelection && canDeleteRow && (
        <Button
          preset="toolbar-delete"
          disabled={selectedDeletableCount === 0 || bulkDeletePending || bulkValidatePending}
          onClick={onBulkDelete}
          tooltipText={getReportBulkDeleteTooltip(selectedDeletableCount)}
        />
      )}
      {enableFilters && (
        <Button
          preset="toolbar-filter"
          className={cn(showFilters && 'section-action-button-active')}
          showBadge={hasActiveFilters}
          onClick={onToggleFilters}
        />
      )}
      {canExport && (
        <Button
          preset="toolbar-export"
          onClick={onExportExcel}
          loading={isExporting}
          disabled={isEmptyForExport}
          tooltipText={getReportExportTooltip(isEmptyForExport, isExporting)}
        />
      )}
    </>
  );
}

/** Toggle which filter groups appear in the configure panel. */
function ReportsConfigureFilters({
  allowedFilterIds,
  filterVisibility,
  isSavingFilterVisibility,
  onToggleVisibility,
}: Readonly<{
  allowedFilterIds: string[];
  filterVisibility: Record<string, boolean | undefined>;
  isSavingFilterVisibility: boolean;
  onToggleVisibility: (id: string) => void;
}>) {
  return (
    <div className="flex flex-wrap gap-3">
      {ALL_FILTER_GROUP_IDS.filter((id) => allowedFilterIds.includes(id)).map((id) => {
        const isOn = filterVisibility[id] ?? true;
        return (
          <Button
            key={id}
            variant="custom"
            size="sm"
            onClick={() => onToggleVisibility(id)}
            disabled={isSavingFilterVisibility}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
              isSavingFilterVisibility && 'opacity-60 cursor-not-allowed',
              isOn
                ? 'bg-[var(--color-2-nuance-1)] border-[var(--color-2-nuance-1)] text-white'
                : 'bg-transparent border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] text-muted-foreground hover:border-[var(--color-1-nuance-2)]',
            )}
          >
            {FILTER_GROUP_LABELS[id]}
          </Button>
        );
      })}
    </div>
  );
}

/** Expandable filters panel with configure chips and result count. */
function ReportsFiltersPanel({
  enableFilters,
  showFilters,
  hasActiveFilters,
  filterGroups,
  onClearFilters,
  resultCount,
  allowedFilterIds,
  showConfigure,
  onToggleConfigure,
  filterVisibility,
  isSavingFilterVisibility,
  onToggleVisibility,
}: Readonly<{
  enableFilters: boolean;
  showFilters: boolean;
  hasActiveFilters: boolean;
  filterGroups: {
    id: string;
    label: string;
    type: 'custom';
    customRender: () => React.ReactNode;
  }[];
  onClearFilters: () => void;
  resultCount: number;
  allowedFilterIds: string[];
  showConfigure: boolean;
  onToggleConfigure: () => void;
  filterVisibility: Record<string, boolean | undefined>;
  isSavingFilterVisibility: boolean;
  onToggleVisibility: (id: string) => void;
}>) {
  if (!enableFilters) return null;
  return (
    <DataTableFilters
      show={showFilters}
      hasActiveFilters={hasActiveFilters}
      filterGroups={filterGroups}
      onClearFilters={onClearFilters}
      resultCount={resultCount}
      resultLabel="compte(s) rendu(s)"
      onToggleConfigure={allowedFilterIds.length > 0 ? onToggleConfigure : undefined}
      showConfigure={showConfigure}
      configureContent={
        <ReportsConfigureFilters
          allowedFilterIds={allowedFilterIds}
          filterVisibility={filterVisibility}
          isSavingFilterVisibility={isSavingFilterVisibility}
          onToggleVisibility={onToggleVisibility}
        />
      }
    />
  );
}

/** Row slot props for view/edit/delete/validate on one report. */
type ReportItemSlotProps = Readonly<{
  report: SessionReport;
  onToggleRow?: (id: string) => void;
  onEditReport?: (sessionId: string, reportId: string, sessionType?: string) => void;
  onDelete: (sessionId: string, reportId: string) => void;
  onValidate: (sessionId: string, reportId: string) => void;
  pageActions: ReportPageActions;
  showBulkSelection: boolean;
  canValidateAny: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
}>;

/** Bind row action handlers and selection flags for one report. */
function getReportItemSlotHandlers(props: ReportItemSlotProps) {
  const selectable = isSessionReportSelectable(
    props.report,
    props.canValidateAny,
    props.pageActions,
  );
  return {
    selectable,
    onView: props.onToggleRow ? () => props.onToggleRow?.(props.report.id) : undefined,
    onEdit: props.onEditReport
      ? () =>
          props.onEditReport?.(props.report.sessionId, props.report.id, props.report.session?.type)
      : undefined,
    onDelete: () => props.onDelete(props.report.sessionId, props.report.id),
    onValidate: () => props.onValidate(props.report.sessionId, props.report.id),
    isSelectable: props.showBulkSelection && selectable,
  };
}

/** Table-row slot that binds view/edit/delete/validate for one report. */
function ReportsRowSlot(props: ReportItemSlotProps) {
  const handlers = getReportItemSlotHandlers(props);
  return (
    <ReportRow
      report={props.report}
      onView={handlers.onView}
      onEdit={handlers.onEdit}
      onDelete={handlers.onDelete}
      onValidate={handlers.onValidate}
      pageActions={props.pageActions}
      isSelectable={handlers.isSelectable}
      isSelected={props.isSelected}
      onToggleSelect={props.onToggleSelect}
    />
  );
}

/** Card slot that binds view/edit/delete/validate for one report. */
function ReportsCardSlot(props: ReportItemSlotProps) {
  const handlers = getReportItemSlotHandlers(props);
  return (
    <ReportCard
      report={props.report}
      onView={handlers.onView}
      onEdit={handlers.onEdit}
      onDelete={handlers.onDelete}
      onValidate={handlers.onValidate}
      pageActions={props.pageActions}
      isSelectable={handlers.isSelectable}
      isSelected={props.isSelected}
      onToggleSelect={props.onToggleSelect}
    />
  );
}

/** Delete, validate, and bulk-action modals for the reports table. */
function ReportsModals({
  deleteId,
  validateId,
  validationTarget,
  bulkDeleteOpen,
  bulkValidateOpen,
  bulkValidationOpen,
  bulkReportValidationOptions,
  reportToDelete,
  reportToValidate,
  reportForValidationModal,
  selectedDeletableReports,
  selectedValidatableReports,
  deletePending,
  validatePending,
  bulkDeletePending,
  bulkValidatePending,
  onCloseDelete,
  onConfirmDelete,
  onCloseValidation,
  onConfirmValidation,
  onCloseBulkValidation,
  onConfirmBulkValidation,
  onCloseValidate,
  onConfirmValidate,
  onCloseBulkDelete,
  onConfirmBulkDelete,
  onCloseBulkValidate,
  onConfirmBulkValidate,
}: Readonly<{
  deleteId: { sessionId: string; reportId: string } | null;
  validateId: { sessionId: string; reportId: string } | null;
  validationTarget: {
    sessionId: string;
    reportId: string;
    validateOptions: ReportValidationOptions;
  } | null;
  bulkDeleteOpen: boolean;
  bulkValidateOpen: boolean;
  bulkValidationOpen: boolean;
  bulkReportValidationOptions: Record<string, ReportValidationOptions>;
  reportToDelete: SessionReport | null;
  reportToValidate: SessionReport | null;
  reportForValidationModal: SessionReport | null;
  selectedDeletableReports: SessionReport[];
  selectedValidatableReports: SessionReport[];
  deletePending: boolean;
  validatePending: boolean;
  bulkDeletePending: boolean;
  bulkValidatePending: boolean;
  onCloseDelete: () => void;
  onConfirmDelete: () => void;
  onCloseValidation: () => void;
  onConfirmValidation: () => void;
  onCloseBulkValidation: () => void;
  onConfirmBulkValidation: () => void;
  onCloseValidate: () => void;
  onConfirmValidate: () => void;
  onCloseBulkDelete: () => void;
  onConfirmBulkDelete: () => void;
  onCloseBulkValidate: () => void;
  onConfirmBulkValidate: () => void;
}>) {
  const deletableCount = selectedDeletableReports.length;
  const validatableCount = selectedValidatableReports.length;

  return (
    <>
      <DeleteModal
        open={!!deleteId}
        onClose={onCloseDelete}
        onConfirm={onConfirmDelete}
        itemName={formatReportSessionModalItemName(reportToDelete)}
        isLoading={deletePending}
      />
      <ReportValidationModal
        open={!!validationTarget}
        onClose={onCloseValidation}
        onConfirm={onConfirmValidation}
        report={reportForValidationModal}
        validateOptions={validationTarget?.validateOptions}
        isLoading={validatePending}
      />
      <ReportValidationModal
        open={bulkValidationOpen}
        onClose={onCloseBulkValidation}
        onConfirm={onConfirmBulkValidation}
        reports={selectedValidatableReports}
        validateOptionsByReportId={bulkReportValidationOptions}
      />
      <ValidateModal
        open={!!validateId}
        onClose={onCloseValidate}
        onConfirm={onConfirmValidate}
        itemName={formatReportSessionModalItemName(reportToValidate)}
        isLoading={validatePending}
      />
      <DeleteModal
        open={bulkDeleteOpen}
        onClose={onCloseBulkDelete}
        onConfirm={onConfirmBulkDelete}
        itemName={formatReportCountLabel(deletableCount)}
        description={getReportBulkDeleteDescription(deletableCount)}
        isLoading={bulkDeletePending}
      />
      <ValidateModal
        open={bulkValidateOpen}
        onClose={onCloseBulkValidate}
        onConfirm={onConfirmBulkValidate}
        itemName={formatReportCountLabel(validatableCount)}
        description={getReportBulkValidateDescription(validatableCount)}
        isLoading={bulkValidatePending}
      />
    </>
  );
}

/** In-person reports list (table + cards + filters). */
export const ReportsDataTable: React.FC<ReportsDataTableProps> = ({
  reports: externalReports,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  expandedRowId,
  onToggleRow,
  onCloseDetails,
  onEditReport,
  enableFilters = true,
  defaultFilters = DEFAULT_REPORT_FILTERS,
  layoutWidth: layoutWidthProp = 'full',
  isSection = false,
  pageActions,
  deepLinkSessionId = null,
  deepLinkSessionGroupId = null,
  activeTab = 'all',
  allowedTabIds,
}) => {
  const isHiddenByWrapper = React.useContext(CollapseReplaceHiddenContext);
  const { user } = useConnectedUser();

  const dt = useDataTable<ReportFilters, string>({
    tableName: 'reports',
    defaultFilters,
    allFilterGroupIds: ALL_FILTER_GROUP_IDS,
    layoutWidth: layoutWidthProp,
    isSection,
    externalViewMode,
    onExternalViewModeChange: externalOnViewModeChange,
    externalExpandedRowId: expandedRowId,
    onCloseDetails,
  });

  // Reset to page 1 and close detail row when tab changes — compare with previous value to skip
  // initial mount (and React 18 StrictMode's double-invoke).
  const prevActiveTabRef = React.useRef(activeTab);
  React.useEffect(() => {
    const prev = prevActiveTabRef.current;
    prevActiveTabRef.current = activeTab;
    if (prev === activeTab) return;
    dt.closeExpandedRow();
    dt.setCurrentPage(1);
  }, [activeTab]);

  const effectiveSessionTypes = React.useMemo(
    () => resolveReportEffectiveSessionTypes(activeTab, allowedTabIds, dt.filters.sessionTypes),
    [activeTab, allowedTabIds, dt.filters.sessionTypes],
  );

  const {
    data: apiResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      'reports',
      dt.currentPage,
      dt.itemsPerPage,
      dt.filters,
      activeTab,
      effectiveSessionTypes,
      deepLinkSessionId,
      deepLinkSessionGroupId,
    ],
    queryFn: async () =>
      getAllReports(
        toReportApiFilterParams(dt.filters, effectiveSessionTypes, {
          page: dt.currentPage,
          pageSize: dt.itemsPerPage,
          sessionId: deepLinkSessionId ?? undefined,
          sessionGroupId: deepLinkSessionGroupId ?? undefined,
          includeSearch: true,
        }),
      ),
    enabled: !externalReports && !isHiddenByWrapper,
  });

  const data = React.useMemo(
    () => resolveReportTableRows(externalReports, apiResponse?.data),
    [externalReports, apiResponse?.data],
  );
  const totalItems = apiResponse?.total || data.length;
  const totalPages = Math.ceil(totalItems / dt.itemsPerPage) || 1;
  const startIndex = (dt.currentPage - 1) * dt.itemsPerPage;
  const endIndex = startIndex + dt.itemsPerPage;

  const [isExporting, setIsExporting] = React.useState(false);
  const isEmptyForExport = React.useMemo(
    () => isReportListEmptyForExport(!!externalReports, data.length, isLoading, totalItems),
    [externalReports, data.length, isLoading, totalItems],
  );

  /** Download the current filter set as Excel (same query as the list, no pagination). */
  const handleExportExcel = React.useCallback(async () => {
    if (isEmptyForExport) {
      toast.warning(getErrorMessage('exportEmpty'));
      return;
    }
    setIsExporting(true);
    try {
      await exportReportsExcel(toReportApiFilterParams(dt.filters, effectiveSessionTypes));
      toast.success('Export Excel réussi !');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsExporting(false);
    }
  }, [dt.filters, isEmptyForExport, effectiveSessionTypes]);

  const expandedReportFromPage = findReportById(data, expandedRowId);
  const sessionIdForExpandedFetch =
    expandedReportFromPage?.session?.id ?? deepLinkSessionId?.trim() ?? null;

  const {
    data: expandedReportData,
    isLoading: isExpandedReportLoading,
    isError: isExpandedReportError,
    error: expandedReportError,
  } = useQuery({
    queryKey: ['reports', sessionIdForExpandedFetch, expandedRowId],
    queryFn: async () => {
      if (!expandedRowId || !sessionIdForExpandedFetch) return null;
      const { getReport } = await import('@/services/inperson/reports.service');
      return getReport(sessionIdForExpandedFetch, expandedRowId);
    },
    enabled: !!expandedRowId && !!sessionIdForExpandedFetch && !isHiddenByWrapper,
    retry: shouldRetryUnitGet,
  });
  useUnitGetNotFound({
    isError: isExpandedReportError,
    error: expandedReportError,
    catalogKey: 'reportNotFound',
    onClose: onCloseDetails,
    entityId: expandedRowId,
  });

  const hookPageActions = usePageActions('reports');
  const pageActionsForRows = React.useMemo(
    () =>
      mergeReportPageActions(
        {
          canEditRowTutoring: hookPageActions.canEditRowTutoring,
          canEditRowInformation: hookPageActions.canEditRowInformation,
          canValidateTutoring: hookPageActions.canValidateTutoring,
          canValidateInformation: hookPageActions.canValidateInformation,
          canDeleteRow: hookPageActions.canDeleteRow,
        },
        pageActions,
      ),
    [
      hookPageActions.canEditRowTutoring,
      hookPageActions.canEditRowInformation,
      hookPageActions.canValidateTutoring,
      hookPageActions.canValidateInformation,
      hookPageActions.canDeleteRow,
      pageActions,
    ],
  );

  const canExport = hookPageActions.canExportCsv;
  const canValidateAny =
    hookPageActions.canValidateTutoring || hookPageActions.canValidateInformation;
  const canDeleteRow = pageActionsForRows.canDeleteRow ?? false;
  const showBulkSelection = canValidateAny || canDeleteRow;

  const actions = useReportActions({
    data,
    userId: user?.id,
    pageActionsForRows,
    canValidateAny,
  });

  const filterHandlers = useReportFilterHandlers(dt);

  const reportToDelete = findReportById(data, actions.deleteId?.reportId);
  const reportToValidate = findReportById(data, actions.validateId?.reportId);
  const reportForValidationModal = findReportById(data, actions.validationTarget?.reportId);

  const hasActiveFilters = hasReportActiveFilters(dt.filters, defaultFilters);

  const filtersContent = React.useMemo(
    () => (
      <ReportFiltersContent
        filters={dt.filters}
        allowedFilterIds={dt.allowedFilterIds}
        visibleFilterIds={dt.visibleFilterIds}
        activeTab={activeTab as any}
        onToggleStatus={filterHandlers.toggleStatusFilter}
        onToggleRating={filterHandlers.toggleRatingFilter}
        onSetDateRange={filterHandlers.setDateRange}
        onSetCustomDateRange={filterHandlers.setCustomDateRange}
        onToggleSessionType={filterHandlers.toggleSessionTypeFilter}
        onSetEstablishment={filterHandlers.setEstablishmentFilter}
        onSetSearch={filterHandlers.setSearch}
      />
    ),
    [dt.filters, dt.allowedFilterIds, dt.visibleFilterIds, activeTab, filterHandlers],
  );

  const filterGroups = React.useMemo(
    () => [
      {
        id: 'filters',
        label: 'Filtres',
        type: 'custom' as const,
        customRender: () => filtersContent,
      },
    ],
    [filtersContent],
  );

  const expandedReport = expandedReportData ?? null;
  const detailLoading = isExpandedReportLoading || !expandedReport;

  const renderReportDetailsPanel = React.useCallback(
    () => <ReportDetails report={expandedReport} isReportLoading={detailLoading} />,
    [expandedReport, detailLoading],
  );

  const itemSlotProps = {
    onToggleRow,
    onEditReport,
    onDelete: actions.handleDeleteClick,
    onValidate: actions.handleValidateClick,
    pageActions: pageActionsForRows,
    showBulkSelection,
    canValidateAny,
  };

  function handleRetry(): void {
    void refetch();
  }

  const listErrorText = isError ? getErrorMessage('loadReportsFailed') : undefined;

  return (
    <DataTable
      data={data}
      totalItems={totalItems}
      isLoading={isLoading}
      viewMode={dt.viewMode}
      onViewModeChange={dt.handleViewModeChange}
      currentPage={dt.currentPage}
      totalPages={totalPages}
      startIndex={startIndex}
      endIndex={endIndex}
      itemLabel="compte(s) rendu(s)"
      onPageChange={dt.setCurrentPage}
      hasActiveFilters={hasActiveFilters}
      containerRef={dt.containerRef}
      expandedColSpan={getReportExpandedColSpan(showBulkSelection)}
      toolbarActions={
        <ReportsToolbar
          showBulkSelection={showBulkSelection}
          canValidateAny={canValidateAny}
          canDeleteRow={canDeleteRow}
          canExport={canExport}
          enableFilters={enableFilters}
          selectedValidatableCount={actions.selectedValidatableReports.length}
          selectedDeletableCount={actions.selectedDeletableReports.length}
          bulkValidatePending={actions.bulkValidateMutation.isPending}
          bulkDeletePending={actions.bulkDeleteMutation.isPending}
          isPreparingBulkValidate={actions.isPreparingBulkValidate}
          showFilters={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          isExporting={isExporting}
          isEmptyForExport={isEmptyForExport}
          onBulkValidate={() => void actions.handleToolbarBulkValidateClick()}
          onBulkDelete={() => actions.setBulkDeleteOpen(true)}
          onToggleFilters={() => dt.setShowFilters((prev) => !prev)}
          onExportExcel={() => void handleExportExcel()}
        />
      }
      filtersPanel={
        <ReportsFiltersPanel
          enableFilters={enableFilters}
          showFilters={dt.showFilters}
          hasActiveFilters={hasActiveFilters}
          filterGroups={filterGroups}
          onClearFilters={dt.clearFilters}
          resultCount={totalItems}
          allowedFilterIds={dt.allowedFilterIds}
          showConfigure={dt.showConfigure}
          onToggleConfigure={() => dt.setShowConfigure((v) => !v)}
          filterVisibility={dt.filterVisibility}
          isSavingFilterVisibility={dt.isSavingFilterVisibility}
          onToggleVisibility={dt.handleToggleFilterVisibility}
        />
      }
      renderTableHead={() => (
        <ReportsTableHead
          showBulkSelection={showBulkSelection}
          allSelectableSelected={actions.allSelectableSelected}
          someSelectableSelected={actions.someSelectableSelected}
          selectableDisabled={actions.selectableIds.length === 0}
          onToggleSelectAll={actions.handleToggleSelectAll}
        />
      )}
      renderTableRow={(report) => (
        <ReportsRowSlot
          report={report}
          isSelected={actions.selection.isSelected(report.id)}
          onToggleSelect={() => actions.selection.toggle(report.id)}
          {...itemSlotProps}
        />
      )}
      expandedRowId={dt.expandedRowId}
      renderExpandedRow={() => renderReportDetailsPanel()}
      renderCard={(report) => (
        <ReportsCardSlot
          report={report}
          isSelected={actions.selection.isSelected(report.id)}
          onToggleSelect={() => actions.selection.toggle(report.id)}
          {...itemSlotProps}
        />
      )}
      emptyIcon={FileText}
      emptyText="Aucun compte rendu trouvé"
      errorText={listErrorText}
      onRetry={handleRetry}
      title="Comptes-rendus"
      sidebarMenuId="menu-reports"
      isSection={isSection}
      expandedItem={expandedReport}
      isExpandedDetailLoading={detailLoading}
      isDetailsPanelOpen={!!expandedRowId}
      onCloseDetails={onCloseDetails}
      renderDetailsPanel={renderReportDetailsPanel}
      modals={
        <ReportsModals
          deleteId={actions.deleteId}
          validateId={actions.validateId}
          validationTarget={actions.validationTarget}
          bulkDeleteOpen={actions.bulkDeleteOpen}
          bulkValidateOpen={actions.bulkValidateOpen}
          bulkValidationOpen={actions.bulkValidationOpen}
          bulkReportValidationOptions={actions.bulkReportValidationOptions}
          reportToDelete={reportToDelete}
          reportToValidate={reportToValidate}
          reportForValidationModal={reportForValidationModal}
          selectedDeletableReports={actions.selectedDeletableReports}
          selectedValidatableReports={actions.selectedValidatableReports}
          deletePending={actions.deleteMutation.isPending}
          validatePending={actions.validateMutation.isPending}
          bulkDeletePending={actions.bulkDeleteMutation.isPending}
          bulkValidatePending={actions.bulkValidateMutation.isPending}
          onCloseDelete={() => actions.setDeleteId(null)}
          onConfirmDelete={actions.handleDeleteConfirm}
          onCloseValidation={() => actions.setValidationTarget(null)}
          onConfirmValidation={actions.handleValidationConfirm}
          onCloseBulkValidation={() => {
            actions.setBulkValidationOpen(false);
            actions.setBulkReportValidationOptions({});
          }}
          onConfirmBulkValidation={actions.handleBulkValidationConfirm}
          onCloseValidate={() => actions.setValidateId(null)}
          onConfirmValidate={actions.handleValidateConfirm}
          onCloseBulkDelete={() => actions.setBulkDeleteOpen(false)}
          onConfirmBulkDelete={actions.handleBulkDeleteConfirm}
          onCloseBulkValidate={() => actions.setBulkValidateOpen(false)}
          onConfirmBulkValidate={actions.handleBulkValidateConfirm}
        />
      }
    />
  );
};
